import _ from 'lodash';

import {
  GAME_ID,
  INIT_TRAY,
  RELOAD_TRAY,
  ADD_TILE_TO_DESK,
  REMOVE_TILE_FROM_DESK,
  REORDER_TILES_ON_DESK,
  CUT_TILE,
  SERVE_ANSWER,
  TURN_SECOND_ATTEMPT_MODE_ON,
  TURN_TRY_AGAIN_MODE_ON,
  REPLACE_DESK_WITH_CORRECT_ANSWER_TILE,
} from '../foodTruckConstants';

import Helper from './foodTruckQuestionReducerHelper';

import { splitPowerPlatesTile } from './foodTruckSelectors';

const initialState = {
  // target word as it is
  targetWord: null,
  // question id, always remains as it is
  questionId: null,
  // uuid for Embedded Measurement
  uuid: null,
  // initial tiles
  initialCollection: [],
  // all tiles in the left and right trays
  trayCollection: [],
  // all tiles on the desk
  deskCollection: [],
  // total length of all desk tiles' words
  deskWeight: 0,
  // current amount of desk tiles
  deskTilesAmount: 0,
  // limit of cut attempts for current level, non-changeable
  cutAttemptsLimit: 0,
  // total amount of remaining cut attempts
  cutAttemptsRemainder: 0,
  // total amount of used cut attempts
  cutAttemptsUsed: 0,
  // total amount of all cut tiles dragged of the desk to tray
  cutTilesOffDeskAmount: 0,
  // user's answer, becomes non-null once user pressed Serve button and cut desk tiles got joined into one tile
  answer: null,
  // becomes true/false once user pressed Serve button and cut desk tiles got joined into one tile
  isAnswerCorrect: null,
  // true when second attempt mode is active
  secondAttemptModeOn: false,
  // true when stry again mode is active
  tryAgainModeOn: false,
  // becomes true, once correct answer appears as tile on the desk
  correctAnswerTilePlaced: false,
};

// most complicated reducer: everything related to question data: trays, tiles, etc
const foodTruckQuestionReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case INIT_TRAY: {
      const {
        question: { targetWord, questionId, uuid, tiles, solutions },
        cutAttemptsLimit,
      } = action.payload;

      let trayCollection = tiles.map(tileText => Helper.getCollectionTile(tileText, {
        solution: solutions.includes(tileText),
      }));

      // distribute tiles between trays
      trayCollection = Helper.getTrayDistributedCollection(trayCollection);

      return {
        ...initialState,
        targetWord,
        questionId,
        uuid,
        initialCollection: trayCollection,
        trayCollection,
        cutAttemptsLimit,
        // initially remainder is the same as limit
        cutAttemptsRemainder: cutAttemptsLimit,
        // and no used attempts apparently
        cutAttemptsUsed: 0,
      };
    }
    case RELOAD_TRAY: {
      return {
        ...initialState,
        targetWord: state.targetWord,
        questionId: state.questionId,
        uuid: state.uuid,
        trayCollection: state.initialCollection,
        initialCollection: state.initialCollection,
        // restore limit
        cutAttemptsLimit: state.cutAttemptsLimit,
        // and restore remainder, again it's the same as limit
        cutAttemptsRemainder: state.cutAttemptsLimit,
        // if it was second attempt mode, it should remain as is
        secondAttemptModeOn: state.secondAttemptModeOn,
      };
    }
    case ADD_TILE_TO_DESK: {
      const trayCollection = state.trayCollection.map(tile => ({
        ...tile,
        // mark tile recently placed on desk as "empty" in tray collection
        movedToDesk: tile.movedToDesk || action.payload.id === tile.id,
      }));

      const tileToAdd = {
        ...action.payload,
      };

      // generate desk tiles collection
      const deskCollection = [
        ...state.deskCollection,
        ...[Helper.getDeskTile(tileToAdd, state.deskCollection.length)],
      ];

      return {
        ...state,
        trayCollection: Helper.getRegeneratedRenderIdCollection(trayCollection),
        deskCollection: Helper.getRegeneratedRenderIdCollection(deskCollection),
        deskWeight: Helper.getCollectionWeight(deskCollection),
        deskTilesAmount: deskCollection.length,
      };
    }
    case REMOVE_TILE_FROM_DESK: {
      const trayCollection = state.trayCollection.map(tile => ({
        ...tile,
        // mark tile recently removed from desk as returned back to tray
        movedToDesk: action.payload.id === tile.id ? false : tile.movedToDesk,
      }));

      // generate desk tiles collection
      const deskCollection = _.reject(
        state.deskCollection,
        tile => tile.id === action.payload.id,
      );

      // if removed tile exists in initial collection, it's initial tile
      const isInitialTile = state.initialCollection.find(tile => tile.id === action.payload.id);

      if (!isInitialTile) {
        trayCollection.push({
          ...action.payload,
          ...Helper.getEasiestTrayProp(trayCollection),
          movedToDesk: false,
        });
      }

      return {
        ...state,
        trayCollection: Helper.getRegeneratedRenderIdCollection(trayCollection),
        deskCollection: Helper.getRegeneratedRenderIdCollection(deskCollection),
        deskWeight: Helper.getCollectionWeight(deskCollection),
        deskTilesAmount: deskCollection.length,
        // increase only if removed tile isn't initial
        cutTilesOffDeskAmount: isInitialTile ? state.cutTilesOffDeskAmount : state.cutTilesOffDeskAmount + 1,
      };
    }
    case REORDER_TILES_ON_DESK: {
      const { deskTileLayouts } = action.payload;

      // sort by x coordinate
      const deskTileLayoutsSorted = _.sortBy(deskTileLayouts, [deskTileLayout => deskTileLayout.x]);

      const deskCollection = deskTileLayoutsSorted.map(
        // regenerate tiles
        (deskTileLayout, deskOrder) => Helper.getDeskTile(
          state.deskCollection.find(tile => deskTileLayout.id === tile.id),
          deskOrder,
        ),
      );

      return {
        ...state,
        deskCollection: Helper.getRegeneratedRenderIdCollection(deskCollection),
      };
    }
    case CUT_TILE: {
      const { letter, currentLevelKey } = action.payload;

      // pick tile which should be cut
      const tileToCut = state.deskCollection.find(item => item.id === letter.deskTileId);

      // remove it from tray collection
      const trayCollection = _.reject(
        state.trayCollection,
        tile => tile.id === tileToCut.id,
      );

      // for power plates we need to cut in different way
      const isPowerPlatesTile = currentLevelKey === 'powerPlates';

      // generate text for 2 new tiles by cutting of original tile's text
      const [leftTileText, rightTileText] = isPowerPlatesTile ? splitPowerPlatesTile(tileToCut.text) : [
        tileToCut.text.slice(0, letter.letterIndex + 1),
        tileToCut.text.slice(letter.letterIndex + 1),
      ];

      // remember where it was on desk
      const deskTileToCutIndex = state.deskCollection.findIndex(
        tile => tile.id === letter.deskTileId,
      );

      // and then then generate tiles themselves, also mark them as moved to desk (because they're on the desk once cut)
      const leftTile = Helper.getCollectionTile(leftTileText, {
        movedToDesk: true,
        solution: Helper.isCutTileSolutionTile(state.targetWord, leftTileText, isPowerPlatesTile),
      });
      const rightTile = Helper.getCollectionTile(rightTileText, {
        movedToDesk: true,
        solution: Helper.isCutTileSolutionTile(state.targetWord, rightTileText, isPowerPlatesTile),
      });

      // add them into desk collection
      const deskCollection = [
        // pick tiles before the cut one
        ...state.deskCollection.slice(0, deskTileToCutIndex),
        Helper.getDeskTile(leftTile),
        Helper.getDeskTile(rightTile),
        // pick tiles after the cut one
        ...state.deskCollection.slice(deskTileToCutIndex + 1),
      ];

      return {
        ...state,
        trayCollection,
        deskCollection: Helper.getRegeneratedRenderIdCollection(deskCollection),
        deskTilesAmount: deskCollection.length,
        // decrease remainder, cause we performed cut
        cutAttemptsRemainder: state.cutAttemptsRemainder - 1,
        // increases used attempts, cause we performed cut
        cutAttemptsUsed: state.cutAttemptsUsed + 1,
      };
    }
    case SERVE_ANSWER: {
      const answer = state.deskCollection.reduce(
        (text, tile) => text + tile.text,
        '',
      );

      const isAnswerCorrect = answer === state.targetWord;

      return {
        ...state,
        answer,
        isAnswerCorrect,
      };
    }
    case TURN_SECOND_ATTEMPT_MODE_ON: {
      const trayCollection = state.trayCollection.map(tile => ({
        ...tile,
        movedToDesk: tile.movedToDesk && tile.solution,
      }));

      const deskCollection = state.deskCollection.filter(tile => tile.solution);

      return {
        ...state,
        trayCollection,
        deskCollection: Helper.getRegeneratedRenderIdCollection(deskCollection),
        deskWeight: Helper.getCollectionWeight(deskCollection),
        deskTilesAmount: deskCollection.length,
        secondAttemptModeOn: true,
        answer: null,
        isAnswerCorrect: null,
      };
    }
    case TURN_TRY_AGAIN_MODE_ON: {
      return {
        ...state,
        tryAgainModeOn: true,
        secondAttemptModeOn: false,
        answer: null,
        isAnswerCorrect: null,
      };
    }
    case REPLACE_DESK_WITH_CORRECT_ANSWER_TILE: {
      const deskCollection = [
        Helper.getDeskTile(Helper.getCollectionTile(state.answer)),
      ];

      return {
        ...state,
        deskCollection,
        correctAnswerTilePlaced: true,
      };
    }
    default:
      return state;
  }
};

export default foodTruckQuestionReducer;
